import { useEffect, useRef, useState } from 'react';
import fire5Sprite from '../Moves/fire5.png';
import explosionSprite from '../Moves/PRAS- Explosions.png';
import './FieryDanceAnimation.css';

/**
 * PROFESSIONAL FIERY DANCE ANIMATION - POKÉMON FIRERED STYLE
 * Fire-type move with authentic Game Boy Advance quality animation
 * 
 * Animation Sequence (Authentic Pokémon Style):
 * 1. Player Preparation (200ms) - Charging stance
 * 2. Flame Ring Formation (400ms) - 6 flames appear around opponent
 * 3. Fiery Dance Rotation (1200ms) - Flames rotate and intensify
 * 4. Final Impact (400ms) - Convergence and explosion
 * 
 * Total Duration: ~2200ms (authentic Pokémon timing)
 */
export default function FieryDanceAnimation({ playerRef, opponentRef, onComplete }) {
  const [animationPhase, setAnimationPhase] = useState('idle');
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const container = document.querySelector('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    // Prefer the actual front sprite for visual centering
    const targetEl = opponentEl.querySelector?.('.wild-front') || opponentEl;
    const targetRect = targetEl.getBoundingClientRect();
    
    // Apply stronger left bias to correct right offset - adjustable per sprite
    const biasX = Number(targetEl.dataset?.hitCenterBiasX ?? opponentEl.dataset?.hitCenterBiasX ?? -25);
    const biasY = Number(targetEl.dataset?.hitCenterBiasY ?? opponentEl.dataset?.hitCenterBiasY ?? 0);
    
    const opponentCenterX = targetRect.left + targetRect.width / 2 - containerRect.left + biasX;
    const opponentCenterY = targetRect.top + targetRect.height / 2 - containerRect.top + biasY;

    // SPRITE CONFIGURATIONS
    const FIRE5_CONFIG = {
      frameWidth: 192,
      frameHeight: 192,
      spriteWidth: 960,
      spriteHeight: 576,
      // Frame 5 (index 4): Large intense flame for main animation (row 1, col 5)
      frame5: { x: 768, y: 0 }, // -768px 0px
      // Frame 6 (index 5): Aftershock flame (row 2, col 1)
      frame6: { x: 0, y: 192 }, // 0px -192px
      // Frame 8: Row 2, Column 3 (clean flame arc)
      frame8: { x: 384, y: 192 }, // -384px -192px
      // Frame 9: Row 2, Column 4 (intense flame arc)
      frame9: { x: 576, y: 192 }  // -576px -192px
    };
    
    const EXPLOSION_CONFIG = {
      frameWidth: 192,
      frameHeight: 192,
      spriteWidth: 768,
      spriteHeight: 576,
      // Explosion frames 0-3 (top row)
      frame0: { x: 0, y: 0 },     // 0px 0px
      frame1: { x: 192, y: 0 },   // -192px 0px
      frame2: { x: 384, y: 0 },   // -384px 0px
      frame3: { x: 576, y: 0 }    // -576px 0px
    };

    const cleanupElements = [];
    const cleanupTimers = [];
    
    console.log('🔥 Fiery Dance Animation - Professional FireRed Style');

    // PHASE 1: PLAYER PREPARATION (0-200ms)
    const preparationTimer = setTimeout(() => {
      setAnimationPhase('preparation');
      console.log('🔥 Phase 1: Player Preparation');
      
      // Player charging stance
      if (playerEl) {
        playerEl.classList.add('fiery-dance-charge');
      }
      
    }, 0);
    cleanupTimers.push(preparationTimer);

    // PHASE 2: FLAME RING FORMATION (200-600ms)
    const flameFormationTimer = setTimeout(() => {
      setAnimationPhase('formation');
      console.log('🔥 Phase 2: Flame Ring Formation');
      
      // Create 6 flames in a perfect circle around opponent (authentic Pokémon style)
      const flameCount = 6;
      const radius = 80; // Distance from opponent center
      
      for (let i = 0; i < flameCount; i++) {
        const angle = (i / flameCount) * Math.PI * 2;
        const angleDeg = Math.round((angle * 180) / Math.PI);
        
        // Staggered appearance for professional effect
        setTimeout(() => {
          const flame = document.createElement('div');
          flame.className = 'fiery-dance-flame';
          // Place at target center; use transform to orbit
          flame.style.left = `${opponentCenterX}px`;
          flame.style.top = `${opponentCenterY}px`;
          flame.style.backgroundImage = `url(${fire5Sprite})`;
          flame.style.backgroundSize = '960px 576px';
          flame.style.backgroundPosition = `-${FIRE5_CONFIG.frame8.x}px -${FIRE5_CONFIG.frame8.y}px`; // Frame 8
          flame.style.setProperty('--flame-index', i);
          // Orbit parameters
          flame.style.transform = `translate(-50%, -50%) rotate(${angleDeg}deg) translateX(${radius}px) scale(0)`;
          flame.style.animation = 'fieryDanceFlameAppear 0.4s ease-out forwards';
          flame.style.setProperty('--angle-start', `${angleDeg}deg`);
          flame.style.setProperty('--radius', `${radius}px`);
          // Set CSS vars for ::after masked glow overlay
          flame.style.setProperty('--fd-sprite-url', `url(${fire5Sprite})`);
          flame.style.setProperty('--fd-sprite-size', '960px 576px');
          flame.style.setProperty('--fd-sprite-pos', `-${FIRE5_CONFIG.frame8.x}px -${FIRE5_CONFIG.frame8.y}px`); // match frame 8
          
          container.appendChild(flame);
          cleanupElements.push(flame);
          
          console.log(`🔥 Flame ${i + 1}/6 formed at angle ${Math.round(angle * 180 / Math.PI)}°`);
        }, i * 80); // 80ms stagger for smoother formation
      }
      
    }, 200);
    cleanupTimers.push(flameFormationTimer);

    // PHASE 3: FIERY DANCE ROTATION (600-1800ms)
    const rotationTimer = setTimeout(() => {
      setAnimationPhase('rotation');
      console.log('🔥 Phase 3: Fiery Dance Rotation');
      
      const flames = container.querySelectorAll('.fiery-dance-flame');
      flames.forEach((flame, index) => {
        // Start orbiting and frame cycling
        flame.classList.add('fiery-dance-orbiting');
        flame.classList.add('fiery-dance-cycle');
        
        // Alternate between frame 8 and 9 for dynamic effect
        const useFrame9 = index % 2 === 0;
        if (useFrame9) {
          flame.style.backgroundPosition = `-${FIRE5_CONFIG.frame9.x}px -${FIRE5_CONFIG.frame9.y}px`; // Frame 9 (intense)
          flame.style.setProperty('--fd-sprite-pos', `-${FIRE5_CONFIG.frame9.x}px -${FIRE5_CONFIG.frame9.y}px`);
        } else {
          flame.style.backgroundPosition = `-${FIRE5_CONFIG.frame8.x}px -${FIRE5_CONFIG.frame8.y}px`; // Frame 8 (clean)
          flame.style.setProperty('--fd-sprite-pos', `-${FIRE5_CONFIG.frame8.x}px -${FIRE5_CONFIG.frame8.y}px`);
        }
        
        // Set rotation timing for smooth circular motion
        const delay = `${index * 0.1}s`;
        // Keep ::after mask animation and frame cycle in phase
        flame.style.setProperty('--fd-anim-delay', delay);
        
        // Add falling leaf-like animation
        flame.classList.add('fiery-dance-falling-leaves');
        
        console.log(`🔥 Flame ${index + 1} rotating with falling animation (Frame ${useFrame9 ? '9' : '8'})`);
      });
      
      // Add opponent glow effect during rotation
      if (opponentEl) {
        opponentEl.classList.add('fiery-dance-target-glow');
      }
      
    }, 600);
    cleanupTimers.push(rotationTimer);

    // PHASE 4: FINAL IMPACT (1800-2200ms)
    const impactTimer = setTimeout(() => {
      setAnimationPhase('impact');
      console.log('💥 Phase 4: Final Impact');
      
      const flames = container.querySelectorAll('.fiery-dance-flame');
      
      // Convergence effect - all flames move to center
      flames.forEach((flame, index) => {
        flame.classList.remove('fiery-dance-orbiting', 'fiery-dance-cycle');
        flame.classList.add('fiery-dance-converging');
        
        // Staggered convergence for dramatic effect
        setTimeout(() => {
          flame.style.transition = 'transform 0.3s ease-in, opacity 0.2s ease-out';
          // Already centered; collapse orbit to center and scale up
          flame.style.transform = 'translate(-50%, -50%) scale(1.5)';
          flame.style.backgroundPosition = `-${FIRE5_CONFIG.frame9.x}px -${FIRE5_CONFIG.frame9.y}px`; // All use Frame 9 for max intensity
          flame.style.setProperty('--fd-sprite-pos', `-${FIRE5_CONFIG.frame9.x}px -${FIRE5_CONFIG.frame9.y}px`);
        }, index * 30);
      });
      
      // Professional explosion sequence using PRAS-Explosions.png
      setTimeout(() => {
        // Remove converging flames
        flames.forEach((flame, index) => {
          flame.style.animation = 'fieryDanceFadeOut 0.2s ease-out forwards';
          setTimeout(() => flame.remove(), 200);
        });
        
        // Create explosion sequence using frames 0-3
        const explosionFrames = [0, 1, 2, 3];
        let frameIndex = 0;
        
        const createExplosionFrame = () => {
          if (frameIndex < explosionFrames.length) {
            const currentFrame = explosionFrames[frameIndex];
            const frameKey = `frame${currentFrame}`;
            const frame = EXPLOSION_CONFIG[frameKey];
            
            const explosion = document.createElement('div');
            explosion.className = 'fiery-dance-explosion';
            explosion.style.left = `${opponentCenterX}px`;
            explosion.style.top = `${opponentCenterY}px`;
            explosion.style.width = `${EXPLOSION_CONFIG.frameWidth}px`;
            explosion.style.height = `${EXPLOSION_CONFIG.frameHeight}px`;
            explosion.style.backgroundImage = `url(${explosionSprite})`;
            explosion.style.backgroundSize = `${EXPLOSION_CONFIG.spriteWidth}px ${EXPLOSION_CONFIG.spriteHeight}px`;
            explosion.style.backgroundPosition = `-${frame.x}px -${frame.y}px`;
            explosion.style.animation = 'fieryDanceExplosionFrame 0.15s ease-out forwards';
            
            container.appendChild(explosion);
            cleanupElements.push(explosion);
            
            console.log(`💥 Explosion Frame ${currentFrame} (${frameIndex + 1}/${explosionFrames.length})`);
            
            // Remove previous explosion frame
            if (frameIndex > 0) {
              const prevExplosions = container.querySelectorAll('.fiery-dance-explosion');
              if (prevExplosions.length > 1) {
                prevExplosions[0].remove();
              }
            }
            
            frameIndex++;
            setTimeout(createExplosionFrame, 150);
          } else {
            // Add aftershock flash using frame 6
            setTimeout(() => {
              const aftershock = document.createElement('div');
              aftershock.className = 'fiery-dance-aftershock';
              aftershock.style.left = `${opponentCenterX}px`;
              aftershock.style.top = `${opponentCenterY}px`;
              aftershock.style.width = `${FIRE5_CONFIG.frameWidth}px`;
              aftershock.style.height = `${FIRE5_CONFIG.frameHeight}px`;
              aftershock.style.backgroundImage = `url(${fire5Sprite})`;
              aftershock.style.backgroundSize = `${FIRE5_CONFIG.spriteWidth}px ${FIRE5_CONFIG.spriteHeight}px`;
              aftershock.style.backgroundPosition = `-${FIRE5_CONFIG.frame6.x}px -${FIRE5_CONFIG.frame6.y}px`;
              aftershock.style.animation = 'fieryDanceAftershock 0.4s ease-out forwards';
              
              container.appendChild(aftershock);
              cleanupElements.push(aftershock);
              
              console.log('💥 Aftershock flash effect');
            }, 100);
          }
        };
        
        createExplosionFrame();
        
        // Opponent hit reaction
        if (opponentEl) {
          opponentEl.classList.remove('fiery-dance-target-glow');
          opponentEl.classList.add('fiery-dance-hit');
        }
        
        // Screen flash effect
        setTimeout(() => {
          const screenFlash = document.createElement('div');
          screenFlash.className = 'fiery-dance-screen-flash';
          screenFlash.style.position = 'fixed';
          screenFlash.style.top = '0';
          screenFlash.style.left = '0';
          screenFlash.style.width = '100vw';
          screenFlash.style.height = '100vh';
          screenFlash.style.background = 'rgba(255, 120, 0, 0.4)';
          screenFlash.style.pointerEvents = 'none';
          screenFlash.style.zIndex = '1000';
          screenFlash.style.animation = 'fieryDanceScreenFlash 0.3s ease-out';
          container.appendChild(screenFlash);
          cleanupElements.push(screenFlash);
          
          // Complete animation
          setTimeout(() => {
            console.log('🔥 Fiery Dance Animation Complete - Professional Design!');
            onComplete();
          }, 400);
        }, 300);
        
      }, 200); // Delay before explosion
      
    }, 1800);
    cleanupTimers.push(impactTimer);

    // Cleanup function for component unmount
    return () => {
      console.log('🔥 Fiery Dance Animation - Cleanup');
      
      // Remove all classes
      if (playerEl) {
        playerEl.classList.remove('fiery-dance-charge');
      }
      if (opponentEl) {
        opponentEl.classList.remove('fiery-dance-target-glow', 'fiery-dance-hit');
      }
      
      // Remove all created elements
      cleanupElements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Clear all timers
      cleanupTimers.forEach(timer => clearTimeout(timer));
    };
    
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system integration
FieryDanceAnimation.moveType = "fire";
